/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Custom HTML5 Embedded Video Player with Theme Tokens (Task 10.4.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useI18nStore } from '../../stores/useI18nStore';
import {
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Maximize2,
  Download,
  Gauge,
  Film,
  Repeat,
  Camera,
  Layers,
} from 'lucide-react';

export interface PlayableVideoData {
  id: string;
  videoUrl: string;
  thumbnailUrl: string;
  duration?: number;
  durationSeconds?: number;
  aspectRatio: '16:9' | '9:16' | '1:1';
  model?: string;
  cameraDirection?: string;
  loop?: boolean;
}

interface CustomVideoPlayerProps {
  video: PlayableVideoData;
  onDownload?: (video: PlayableVideoData) => void;
}

const PLAYBACK_SPEEDS = [0.5, 1.0, 1.5, 2.0];

export const CustomVideoPlayer: React.FC<CustomVideoPlayerProps> = ({
  video,
  onDownload,
}) => {
  const { t } = useI18nStore();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(video.duration || video.durationSeconds || 5);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [playbackRate, setPlaybackRate] = useState<number>(1.0);
  const [isLooping, setIsLooping] = useState<boolean>(video.loop ?? false);
  const [showControls, setShowControls] = useState<boolean>(true);
  const controlsTimeoutRef = useRef<number | null>(null);

  // Sync loop state when video prop changes
  useEffect(() => {
    setIsLooping(video.loop ?? false);
    setCurrentTime(0);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.load();
    }
  }, [video.id, video.loop, video.videoUrl]);

  // Handle play / pause toggle
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused || videoRef.current.ended) {
      videoRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  // Handle timeline time update
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
  };

  // Handle video loaded metadata
  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    if (videoRef.current.duration && !isNaN(videoRef.current.duration)) {
      setDuration(videoRef.current.duration);
    }
  };

  // Handle seeking along timeline
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetTime = parseFloat(e.target.value);
    setCurrentTime(targetTime);
    if (videoRef.current) {
      videoRef.current.currentTime = targetTime;
    }
  };

  // Toggle audio mute
  const toggleMute = () => {
    if (!videoRef.current) return;
    const nextMuted = !isMuted;
    videoRef.current.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  // Change playback speed
  const changeSpeed = (speed: number) => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = speed;
    setPlaybackRate(speed);
  };

  // Toggle video loop
  const toggleLoop = () => {
    if (!videoRef.current) return;
    const nextLoop = !isLooping;
    videoRef.current.loop = nextLoop;
    setIsLooping(nextLoop);
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  // Auto-hide controls bar when hovering stops
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      window.clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = window.setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 2800);
  };

  const formatSeconds = (sec: number): string => {
    if (isNaN(sec)) return '00:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Handle MP4 file download
  const handleDownload = () => {
    if (onDownload) {
      onDownload(video);
      return;
    }
    const a = document.createElement('a');
    a.href = video.videoUrl;
    a.download = `sophia-video-${video.id}.mp4`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Aspect ratio helper styling class
  const getAspectClass = () => {
    if (video.aspectRatio === '9:16') return 'aspect-[9/16] max-h-[600px] mx-auto';
    if (video.aspectRatio === '1:1') return 'aspect-square max-h-[520px] mx-auto';
    return 'aspect-video w-full';
  };

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      className="relative overflow-hidden rounded-2xl border border-surface-border bg-black shadow-glass flex flex-col justify-center items-center group select-none"
    >
      {/* 1. Header Badges Overlay */}
      <div
        className={`absolute top-0 left-0 right-0 z-20 p-4 bg-gradient-to-b from-black/80 via-black/40 to-transparent transition-opacity duration-300 flex items-center justify-between ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/20 text-primary border border-primary/30 backdrop-blur-md">
            <Film className="w-3.5 h-3.5" />
            <span>{video.model}</span>
          </span>

          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono font-medium bg-white/10 text-white/90 border border-white/20 backdrop-blur-md">
            {video.duration || video.durationSeconds || 5}s
          </span>

          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono font-medium bg-white/10 text-white/90 border border-white/20 backdrop-blur-md">
            <Layers className="w-3 h-3" />
            {video.aspectRatio}
          </span>

          {video.cameraDirection && video.cameraDirection !== 'static' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono font-medium bg-accent/20 text-accent border border-accent/30 backdrop-blur-md">
              <Camera className="w-3 h-3" />
              {video.cameraDirection}
            </span>
          )}
        </div>

        <button
          onClick={handleDownload}
          title={t('video.downloadMp4')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-medium border border-white/20 transition-colors backdrop-blur-md"
        >
          <Download className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{t('video.downloadMp4')}</span>
        </button>
      </div>

      {/* 2. Main HTML5 Video Canvas */}
      <div className={`w-full flex items-center justify-center ${getAspectClass()}`}>
        <video
          ref={videoRef}
          src={video.videoUrl}
          poster={video.thumbnailUrl}
          loop={isLooping}
          muted={isMuted}
          playsInline
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
          onClick={togglePlay}
          className="w-full h-full object-contain cursor-pointer"
        />
      </div>

      {/* 3. Center Play/Pause Splash Overlay */}
      {!isPlaying && (
        <button
          onClick={togglePlay}
          aria-label={t('video.featuredPlayerTitle')}
          className="absolute inset-0 m-auto w-16 h-16 rounded-full bg-primary/90 hover:bg-primary text-primary-fg flex items-center justify-center shadow-glow-primary transition-transform duration-200 hover:scale-110 active:scale-95 z-10 backdrop-blur-sm"
        >
          <Play className="w-7 h-7 ml-1 fill-current" />
        </button>
      )}

      {/* 4. Bottom Controls Bar */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-20 p-3 sm:p-4 bg-gradient-to-t from-black/90 via-black/60 to-transparent transition-opacity duration-300 space-y-2 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Timeline Progress Slider */}
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={duration || 1}
            step={0.05}
            value={currentTime}
            onChange={handleSeek}
            aria-label="Video timeline scrubber"
            className="w-full h-1.5 bg-white/20 hover:bg-white/30 rounded-lg appearance-none cursor-pointer accent-primary transition-all"
          />
          <span className="text-[11px] font-mono text-white/90 whitespace-nowrap min-w-[70px] text-right">
            {formatSeconds(currentTime)} / {formatSeconds(duration)}
          </span>
        </div>

        {/* Action Controls Row */}
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Play/Pause */}
            <button
              onClick={togglePlay}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <Pause className="w-4 h-4 fill-current" />
              ) : (
                <Play className="w-4 h-4 fill-current" />
              )}
            </button>

            {/* Restart from beginning */}
            <button
              onClick={() => {
                if (videoRef.current) {
                  videoRef.current.currentTime = 0;
                  videoRef.current.play();
                  setIsPlaying(true);
                }
              }}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              title="Replay from start"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            {/* Mute/Unmute */}
            <button
              onClick={toggleMute}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              title={isMuted ? t('video.unmute') : t('video.mute')}
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Loop Toggle */}
            <button
              onClick={toggleLoop}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                isLooping
                  ? 'bg-primary/25 border-primary/40 text-primary'
                  : 'bg-white/10 border-white/20 text-white/70 hover:text-white'
              }`}
              title={t('video.toggleLoop')}
            >
              <Repeat className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t('video.toggleLoop')}</span>
            </button>

            {/* Playback Speed selector */}
            <div className="flex items-center gap-1 bg-white/10 border border-white/20 rounded-lg p-0.5">
              <Gauge className="w-3 h-3 text-white/60 ml-1 hidden sm:inline" />
              {PLAYBACK_SPEEDS.map((speed) => (
                <button
                  key={speed}
                  onClick={() => changeSpeed(speed)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-medium transition-colors ${
                    playbackRate === speed
                      ? 'bg-primary text-primary-fg font-bold'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              title={t('video.fullscreen')}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
